import React from 'react';
import { Body } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { Card } from 'antd';
import { Rule, Ruleset } from 'types/dt-graphql-apollo-v2';
import { getRulesetRules, term } from 'utils/project-ruleset';
import { LabelListDisplay } from 'components';
import { RulesetOptions } from './RulesetOptions';
import { getDisabledRulesCount } from '../helpers';

type RulesetCardsProps = {
  onDeleteRulesetComplete: VoidFunction;
  onEditRuleset: (ruleset: Ruleset) => void;
  onViewRuleset: (ruleset: Ruleset) => void;
  rules: Rule[];
  rulesets: Ruleset[];
};

export const RulesetCards = ({
  onDeleteRulesetComplete,
  onEditRuleset,
  onViewRuleset,
  rules,
  rulesets,
}: RulesetCardsProps) => {
  const renderRulesetOptionsMenu = (ruleset: Ruleset) => (
    <RulesetOptions
      ruleset={ruleset}
      onEditRuleset={onEditRuleset}
      onDeleteRulesetComplete={onDeleteRulesetComplete}
    />
  );

  return (
    <CardContainer>
      {rulesets.map((ruleset) => {
        const rulesetRules = getRulesetRules(ruleset.externalId, rules);
        const rulesCount = rulesetRules.length;
        const disabledRuleCount = getDisabledRulesCount(rulesetRules);

        return (
          <StyledCard
            key={`card-${ruleset.externalId}`}
            title={ruleset.name}
            extra={renderRulesetOptionsMenu(ruleset)}
            style={{ width: 230 }}
            onClick={() => onViewRuleset(ruleset)}
          >
            <Group>
              <StyledDescriptionItalic level={2}>
                {`${rulesCount} ${rulesCount === 1 ? term.rule : term.rules}`}{' '}
                {disabledRuleCount > 0 && ` (${disabledRuleCount} disabled)`}
              </StyledDescriptionItalic>
            </Group>
            <LabelListDisplay
              labels={ruleset.labels?.map((label) => ({
                text: label,
                key: label,
              }))}
              labelSize="medium"
              limitLabels={5}
            />
          </StyledCard>
        );
      })}
    </CardContainer>
  );
};

const Group = styled.div`
  width: 100%;
`;

const CardContainer = styled.div`
  display: grid;
  gap: 1rem;
  grid-auto-rows: max-content;
  grid-template-columns: repeat(auto-fill, 230px);
  margin-top: 1rem;
`;

const StyledCard = styled(Card)`
  &:hover {
    cursor: pointer;
  }
  box-shadow:
    0px 0px 2px 1px rgba(0, 0, 0, 0.04),
    0px 3px 8px rgba(0, 0, 0, 0.06);
  border-radius: 5px;
  .ant-card-head {
    padding: 1rem 0.5rem 0 1.5rem;
    min-height: 1px;
    border-bottom: none;
    .ant-card-head-title,
    .ant-card-extra {
      padding: 5px 0;
      font-style: normal;
      font-weight: 600;
      font-size: 18px;
      line-height: 24px;
    }
  }
  .ant-card-body {
    padding: 0 1.5rem 0.5rem 1.5rem;
    gap: 1rem;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
  }
`;

const StyledDescription = styled(Body)`
  max-height: 4rem;
  max-width: 100%;
  overflow-x: hidden;
  word-break: break-word;
  hyphens: auto;
  display: inline-block;
  overflow-y: auto;
`;

const StyledDescriptionItalic = styled(StyledDescription)`
  font-style: italic;
`;
